import { useState, useEffect, useCallback } from 'react';
import type { FormEvent, ChangeEvent } from 'react';
import { TrashIcon, PencilIcon } from '@heroicons/react/24/solid';
import { moderatorApi } from '../utils/api';
import AssistantsManagement from '../components/AssistantsManagement';
import {
  fromApiPrivileges,
  selectedPrivilegeKeys,
  toApiPrivileges,
  type PrivilegeKey,
} from '../types/moderator';

type ActiveTab = 'moderators' | 'assistants';

interface Moderator {
  id: string;
  name: string;
  email: string;
  status: 'active' | 'inactive';
  privileges: PrivilegeKey[];
  createdAt: string;
}

interface FormData {
  name: string;
  email: string;
  password: string;
  privileges: Record<PrivilegeKey, boolean>;
}

const emptyPrivileges: Record<PrivilegeKey, boolean> = {
  manage_courses: false,
  manage_students: false,
  manage_settings: false,
  manage_content: false,
};

const SystemUsers = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('moderators');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingModerator, setEditingModerator] = useState<Moderator | null>(null);
  const [moderatorToDelete, setModeratorToDelete] = useState<Moderator | null>(null);
  const [moderatorToToggle, setModeratorToToggle] = useState<Moderator | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moderators, setModerators] = useState<Moderator[]>([]);
  const [, setTotalCount] = useState(0);
  const [, setTotalPages] = useState(1);
  const [currentPage] = useState(1);
  const [pageSize] = useState(10);
  const [filteredModerators, setFilteredModerators] = useState<Moderator[]>([]);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    privileges: { ...emptyPrivileges },
  });

  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchModerators = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await moderatorApi.getAll(currentPage, pageSize);

      const mappedModerators: Moderator[] = data.map((mod) => ({
        id: mod.id,
        name: mod.username,
        email: mod.email,
        status: mod.active ? 'active' : 'inactive',
        privileges: fromApiPrivileges(mod.privileges),
        createdAt: mod.createdAt,
      }));

      setModerators(mappedModerators);
      setFilteredModerators(mappedModerators);
      setTotalCount(mappedModerators.length);
      setTotalPages(Math.ceil(mappedModerators.length / pageSize));
    } catch (err) {
      console.error('Error fetching moderators:', err);
      setError(err instanceof Error ? err.message : 'Failed to load moderators. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchModerators();
  }, [fetchModerators]);

  useEffect(() => {
    const filtered = moderators.filter(
      (moderator) =>
        moderator.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moderator.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredModerators(filtered);
    setTotalCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  }, [moderators, searchTerm, pageSize]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    const privilegeKeys = selectedPrivilegeKeys(formData.privileges);
    if (privilegeKeys.length === 0) {
      setError('Please select at least one privilege');
      return;
    }

    const privileges = toApiPrivileges(privilegeKeys);

    try {
      setIsLoading(true);

      if (editingModerator) {
        await moderatorApi.update(editingModerator.id, {
          name: formData.name,
          email: formData.email,
          privileges,
        });

        setSuccessMessage('Moderator updated successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
        await fetchModerators();
        setShowEditForm(false);
        setEditingModerator(null);
      } else {
        await moderatorApi.add({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          privileges,
        });

        setSuccessMessage('Moderator added successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
        await fetchModerators();
        setShowAddForm(false);
      }
    } catch (err) {
      console.error('Error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }

    setFormData({
      name: '',
      email: '',
      password: '',
      privileges: { ...emptyPrivileges },
    });
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev: FormData) => {
      if (type === 'checkbox') {
        return {
          ...prev,
          privileges: {
            ...prev.privileges,
            [name]: checked,
          },
        };
      }
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const handleEditModerator = (moderator: Moderator) => {
    setEditingModerator(moderator);

    setFormData({
      name: moderator.name,
      email: moderator.email,
      password: '',
      privileges: {
        manage_courses: moderator.privileges.includes('manage_courses'),
        manage_students: moderator.privileges.includes('manage_students'),
        manage_settings: moderator.privileges.includes('manage_settings'),
        manage_content: moderator.privileges.includes('manage_content'),
      },
    });

    setShowEditForm(true);
  };

  const handleDeleteModerator = async (id: string) => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);

      const result = await moderatorApi.delete(id);

      setModerators(moderators.filter((mod) => mod.id !== id));
      setModeratorToDelete(null);
      setSuccessMessage(result.data || 'Moderator deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchModerators();
    } catch (err) {
      console.error('Error deleting moderator:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while deleting the moderator');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = (id: string) => {
    setModerators(
      moderators.map((mod) =>
        mod.id === id
          ? { ...mod, status: mod.status === 'active' ? 'inactive' : 'active' }
          : mod
      )
    );
    setModeratorToToggle(null);
  };

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
  };

  const privilegeLabels: Record<PrivilegeKey, string> = {
    manage_courses: 'Manage Courses',
    manage_students: 'Manage Students',
    manage_settings: 'Manage Settings',
    manage_content: 'Manage Codes',
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h1 className="text-2xl font-semibold text-gray-900">مستخدمي النظام</h1>
            {activeTab === 'moderators' && (
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                إضافة مشرف
              </button>
            )}
          </div>
          <div className="mt-4 border-b border-gray-200">
            <nav className="-mb-px flex gap-6">
              <button
                type="button"
                onClick={() => setActiveTab('moderators')}
                className={`py-3 px-1 border-b-2 text-sm font-medium ${
                  activeTab === 'moderators'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                المشرفون
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('assistants')}
                className={`py-3 px-1 border-b-2 text-sm font-medium ${
                  activeTab === 'assistants'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                المساعدون
              </button>
            </nav>
          </div>
        </div>
      </div>

      {activeTab === 'assistants' ? (
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <AssistantsManagement />
        </div>
      ) : (
        <>
          <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <div className="bg-white px-4 py-5 border-b border-gray-200 sm:px-6 rounded-t-lg shadow-sm">
              <form onSubmit={handleSearch} className="flex gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                    placeholder="Search by name or email..."
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                <button
                  type="submit"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Search
                </button>
              </form>
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-4 mt-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {successMessage && (
              <div className="rounded-md bg-green-50 p-4 mt-4">
                <div className="flex">
                  <div className="ml-3">
                    <p className="text-sm font-medium text-green-800">{successMessage}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4">
              <div className="flex flex-col">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Name
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Email
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Status
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Privileges
                            </th>
                            <th
                              scope="col"
                              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {isLoading ? (
                            <tr>
                              <td colSpan={5} className="px-6 py-4 whitespace-nowrap text-center">
                                Loading...
                              </td>
                            </tr>
                          ) : filteredModerators.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-6 py-4 whitespace-nowrap text-center">
                                No moderators found
                              </td>
                            </tr>
                          ) : (
                            filteredModerators.map((moderator) => (
                              <tr key={moderator.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{moderator.name}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-500">{moderator.email}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span
                                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      moderator.status === 'active'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}
                                  >
                                    {moderator.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {moderator.privileges.map((privilege) => (
                                    <span
                                      key={privilege}
                                      className="mr-2 px-2 py-1 bg-gray-100 rounded-full text-xs"
                                    >
                                      {privilegeLabels[privilege] ||
                                        privilege
                                          .split('_')
                                          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                          .join(' ')}
                                    </span>
                                  ))}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={() => handleEditModerator(moderator)}
                                    className="text-indigo-600 hover:text-indigo-900 mr-4"
                                  >
                                    <PencilIcon className="h-5 w-5" />
                                  </button>
                                  <button
                                    onClick={() => setModeratorToDelete(moderator)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <TrashIcon className="h-5 w-5" />
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {(showAddForm || showEditForm) && (
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {showAddForm ? 'Add New Moderator' : 'Edit Moderator'}
                </h3>
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                    </div>
                    {showAddForm && (
                      <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                          Password
                        </label>
                        <input
                          type="password"
                          name="password"
                          id="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Privileges</label>
                      <div className="space-y-2">
                        {Object.entries(privilegeLabels).map(([key, label]) => (
                          <div key={key} className="flex items-center">
                            <input
                              type="checkbox"
                              id={key}
                              name={key}
                              checked={formData.privileges[key as PrivilegeKey]}
                              onChange={handleInputChange}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <label htmlFor={key} className="ml-2 block text-sm text-gray-900">
                              {label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddForm(false);
                        setShowEditForm(false);
                        setEditingModerator(null);
                      }}
                      className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {isLoading ? 'Processing...' : showAddForm ? 'Add Moderator' : 'Update Moderator'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {moderatorToDelete && (
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Moderator</h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to delete {moderatorToDelete.name}? This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setModeratorToDelete(null)}
                    className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteModerator(moderatorToDelete.id)}
                    disabled={isLoading}
                    className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {isLoading ? 'Processing...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {moderatorToToggle && (
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {moderatorToToggle.status === 'active' ? 'Deactivate' : 'Activate'} Moderator
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to{' '}
                  {moderatorToToggle.status === 'active' ? 'deactivate' : 'activate'}{' '}
                  {moderatorToToggle.name}?
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setModeratorToToggle(null)}
                    className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleStatus(moderatorToToggle.id)}
                    className={`py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                      moderatorToToggle.status === 'active'
                        ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                        : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50`}
                  >
                    {moderatorToToggle.status === 'active' ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SystemUsers;
